import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import "./ScheduleMeeting.css";
import PaymentForm, { PAYMENT_DETAILS_DEFAULT, validatePaymentDetails } from "../../../Common/PaymentForm/PaymentForm";
import scheduleMeetingImg from "../../../../assets/Static/Schedule Meeting/Schedule Meeting.png";
import receiptLogoSrc from "../../../../assets/PNG/RecieptLogo.png";
import Login from "../../../Pages/Users/Login.jsx";
import { spendRewardMoney, getWallet } from "../../../Pages/Users/ProfileLogic.js";
import { useAuth } from "../../../../context/AuthContext";
import { generateReceiptHTML } from "../../../../receipt/receipt.js";
import {
  STEPS,
  FORM_DEFAULTS,
  EXPERTS,
  TRUST_BADGES,
  COMPANY_INFO,
  PAYMENT_METHODS,
  GST_RATE,
  getMinDate,
  getMaxDate,
  isDayOff,
  genBookingId,
  saveBooking,
  formatDateLong,
  formatDateShort,
  isValidEmail,
  getConsentForm,
  PAGE_CONTENT,
  REWARD_CONSTANTS,
  FORM_LABELS,
  PLACEHOLDERS,
  SUMMARY_LABELS,
  PRICE_LABELS,
  REWARD_LABELS,
  RECEIPT_LABELS,
  SPEND_DESCRIPTIONS,
  IMAGE_ALT,
  MISC_LABELS,
  MEETING_TYPES as MEETING_TYPES_FALLBACK,
  DURATIONS as DURATIONS_FALLBACK,
  SLOTS as SLOTS_FALLBACK,
  COUNTRIES as COUNTRY_CODES_FALLBACK,
} from "./ScheduleMeetingConstants";
import configService from "../../../../services/configService";
import { resolveIcon } from "../../../Main/Footer/FooterData";

export default function ScheduleMeeting() {
  const { user } = useAuth();
  const [meetingTypes, setMeetingTypes] = useState(MEETING_TYPES_FALLBACK);
  const [durations, setDurations] = useState(DURATIONS_FALLBACK);
  const [slots, setSlots] = useState(SLOTS_FALLBACK);
  const [countryCodes, setCountryCodes] = useState(COUNTRY_CODES_FALLBACK);
  const [contactDetails, setContactDetails] = useState([]);
  const [consentFree, setConsentFree] = useState([]);
  const [consentPaid, setConsentPaid] = useState([]);
  const [step,        setStep]        = useState(1);
  const [meetingType, setMeetingType] = useState("");
  const [duration,    setDuration]    = useState("");
  const [date,        setDate]        = useState("");
  const [slot,        setSlot]        = useState("");
  const [form,        setForm]        = useState(FORM_DEFAULTS);
  const [consent,       setConsent]       = useState(false);
  const [paymentMethod,  setPaymentMethod]  = useState("");
  const [paymentDetails, setPaymentDetails] = useState(PAYMENT_DETAILS_DEFAULT);
  const [submitted,   setSubmitted]   = useState(null);
  const [error,       setError]       = useState("");
  const [rewardDiscount, setRewardDiscount] = useState("");
  const [payByReward, setPayByReward] = useState(false);
  const [showLoginOverlay, setShowLoginOverlay] = useState(false);

  useEffect(() => {
    configService.getConfig().then(r => {
      if (r.data?.meetingTypes) setMeetingTypes(r.data.meetingTypes);
      if (r.data?.durations) setDurations(r.data.durations.map((d, i) => ({ ...d, id: d.id || `dur_${i}` })));
      if (r.data?.slots) setSlots(r.data.slots.map(s => (s && typeof s === 'object' ? s.time : s) ?? ''));
      if (r.data?.countryCodes?.length) setCountryCodes(r.data.countryCodes);
      if (r.data?.contactDetails?.length) {
        setContactDetails(r.data.contactDetails);
        // Build dynamic form defaults from contactDetails
        const defaults = { phoneCode: '+91' };
        for (const field of r.data.contactDetails) {
          if (field.key === 'phone') {
            defaults.phone = '';
          } else {
            defaults[field.key] = '';
          }
        }
        setForm(defaults);
      }
      if (r.data?.consentFree?.length) setConsentFree(r.data.consentFree);
      if (r.data?.consentPaid?.length) setConsentPaid(r.data.consentPaid);
    }).catch(() => {});
  }, []);

  // Compute form defaults based on contactDetails (used in reset)
  function getFormDefaults() {
    if (contactDetails.length > 0) {
      const defaults = { phoneCode: '+91' };
      for (const field of contactDetails) {
        if (field.key === 'phone') {
          defaults.phone = '';
        } else {
          defaults[field.key] = '';
        }
      }
      return defaults;
    }
    return FORM_DEFAULTS;
  }
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [rewardBalance, setRewardBalance] = useState(0);
  const [nonRedeemableBalance, setNonRedeemableBalance] = useState(0);

  const setF = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const setPayF = (e) => setPaymentDetails(d => ({ ...d, [e.target.name]: e.target.value }));

  // ── Derived ────────────────────────────────
  const selectedType   = meetingTypes.find(t => t.id === meetingType);
  const selectedExpert = EXPERTS.find(e => e.specialization.includes(meetingType)) || EXPERTS[0];
  const selectedDuration = durations.find(d => d.id === duration);

  // ── Price calculation ──────────────────────
  const baseAmount = selectedDuration?.amount || 0;
  const convFee = PAYMENT_METHODS.find(p => p.id === paymentMethod)?.convFee || 0;
  const gstAmount = Math.round((baseAmount + convFee) * (GST_RATE / 100));
  const preTaxAmount = baseAmount;
  const totalAmount = baseAmount + convFee + gstAmount;

  // ── Step navigation ────────────────────────
  function goNext(from) {
    setError("");
    setStep(from + 1);
  }

  function goBack(from) {
    setError("");
    setStep(from - 1);
  }

  // ── Step 1 validation ──────────────────────
  function handleStep1Next() {
    if (!meetingType)  { setError(PAGE_CONTENT.validation.selectMeetingType); return; }
    setError("");
    goNext(1);
  }

  // ── Step 2 validation ──────────────────────
  function handleStep2Next() {
    if (!duration)               { setError(PAGE_CONTENT.validation.selectDuration); return; }
    if (!date)                   { setError(PAGE_CONTENT.validation.selectDate); return; }
    if (isDayOff(date))          { setError(PAGE_CONTENT.validation.weekendClosed); return; }
    if (!slot)                   { setError(PAGE_CONTENT.validation.selectSlot); return; }
    setError("");
    goNext(2);
  }

  // ── Step 3 validation (contact details) ─────────────────
  function handleStep3Next() {
    // Dynamic validation based on contactDetails from backend
    if (contactDetails.length > 0) {
      for (const field of contactDetails) {
        const val = (form[field.key] || '').trim();
        if (field.required === 'Yes' && !val) {
          setError(`Please enter your ${field.label.toLowerCase().replace(/\s*\*?$/, '')}.`);
          return;
        }
        if (field.key === 'email' && val && !isValidEmail(val)) {
          setError(PAGE_CONTENT.validation.validEmail);
          return;
        }
        if (field.key === 'phone' && val && val.replace(/\D/g, '').length !== 10) {
          setError("Please enter a valid 10-digit phone number.");
          return;
        }
      }
    } else {
      // Fallback validation using hardcoded fields
      const { name, email, phone } = form;
      if (!name.trim())               { setError(PAGE_CONTENT.validation.nameRequired); return; }
      if (!isValidEmail(email))       { setError(PAGE_CONTENT.validation.validEmail); return; }
      if (!phone.trim() || phone.replace(/\D/g, '').length !== 10)  { setError("Please enter a valid 10-digit phone number."); return; }
    }
    goNext(3);
    setError('');
  }

  // ── Step 4 validation (review booking) ─────────────────
  function handleStep4Next() {
    goNext(4);
  }

  // ── Step 5 validation (consent) ─────────────────
  async function handleStep5Next() {
    if (!consent)                 { setError(PAGE_CONTENT.validation.agreeToTerms); return; }
    
    const selectedDuration = durations.find(d => d.id === duration);
    if (selectedDuration?.price === "Free") {
      const booking = {
        id:          genBookingId(),
        type:        meetingType,
        typeLabel:   selectedType?.label || meetingType,
        duration,
        durationLabel: selectedDuration?.label,
        paymentMethod: "Free",
        paymentInfo: {},
        baseAmount: 0,
        convFee: 0,
        gstAmount: 0,
        totalAmount: 0,
        date,
        slot,
        expert:      selectedExpert?.name,
        ...form,
        bookedAt:    new Date().toISOString(),
      };
      try {
        await saveBooking(booking, user?.email);
        setSubmitted(booking);
        setStep(7);
      } catch (err) {
        const data = err?.response?.data;
      const msgs = data?.errors?.join("; ") || data?.message || err?.message || "Booking failed";
      setError(msgs);
      return;
      }
    } else {
      goNext(5);
    }
    setError("");
  }

  // ── Step 6 validation (payment) ─────────────────
  async function handleStep6Next() {
    const session = user;

    if (payByReward || paymentMethod === "reward") {
      if (!session) { setError(REWARD_CONSTANTS.VALIDATION_MESSAGES.NOT_LOGGED_IN); return; }
      if (totalRewardBalance < totalAmount) {
        setError(REWARD_CONSTANTS.VALIDATION_MESSAGES.INSUFFICIENT_BALANCE
          .replace("{required}", totalAmount)
          .replace("{available}", totalRewardBalance));
        return;
      }
      const fullPrice = totalAmount;
      spendRewardMoney(
        session.email,
        fullPrice,
        SPEND_DESCRIPTIONS.payByReward
      );
      const selectedDuration = durations.find(d => d.id === duration);
      const booking = {
        id:          genBookingId(),
        type:        meetingType,
        typeLabel:   selectedType?.label || meetingType,
        duration,
        durationLabel: selectedDuration?.label,
        paymentMethod: "Pay by Reward",
        paymentInfo: { rewardUsed: fullPrice },
        baseAmount,
        convFee,
        gstAmount,
        totalAmount: fullPrice,
        rewardDeduction: fullPrice,
        date,
        slot,
        expert:      selectedExpert?.name,
        ...form,
        bookedAt:    new Date().toISOString(),
      };
      try {
        await saveBooking(booking, user?.email);
      } catch (err) {
        const data = err?.response?.data;
        const msgs = data?.errors?.join("; ") || data?.message || err?.message || "Booking failed";
        setError(msgs);
        return;
      }
      loadWalletData();
      setSubmitted(booking);
      setStep(7);
      setError("");
      return;
    }

    const usingRewardDiscount = rewardDiscountNum > 0;
    if (usingRewardDiscount) {
      if (!session) { setError(REWARD_CONSTANTS.VALIDATION_MESSAGES.NOT_LOGGED_IN); return; }
      const err = validateRewardDiscount(rewardDiscountNum);
      if (err) { setError(err); return; }
    }

    const detailErr = validatePaymentDetails(paymentMethod, paymentDetails);
    if (detailErr) { setError(detailErr); return; }

    if (usingRewardDiscount && session) {
      spendRewardMoney(
        session.email,
        rewardDiscountNum,
        SPEND_DESCRIPTIONS.rewardDiscount
      );
    }

    const selectedDuration = durations.find(d => d.id === duration);
    const selectedPayment = PAYMENT_METHODS.find(p => p.id === paymentMethod);
    const booking = {
      id:          genBookingId(),
      type:        meetingType,
      typeLabel:   selectedType?.label || meetingType,
      duration,
      durationLabel: selectedDuration?.label,
      paymentMethod,
      paymentInfo: {
        upiId: paymentDetails.upiId || "",
        bankName: paymentDetails.bankName || "",
        cardLast4: (paymentDetails.cardNum || "").replace(/\D/g, "").slice(-4),
        rewardDiscount: usingRewardDiscount ? rewardDiscountNum : 0,
      },
      baseAmount,
      convFee,
      gstAmount,
      totalAmount: finalPayable,
      rewardDiscount: usingRewardDiscount ? rewardDiscountNum : 0,
      rewardDeduction: usingRewardDiscount ? rewardDiscountNum : 0,
      date,
      slot,
      expert:      selectedExpert?.name,
      ...form,
      bookedAt:    new Date().toISOString(),
    };
    try {
      await saveBooking(booking, user?.email);
    } catch (err) {
      const data = err?.response?.data;
      const msgs = data?.errors?.join("; ") || data?.message || err?.message || "Booking failed";
      setError(msgs);
      return;
    }
    loadWalletData();
    setSubmitted(booking);
    setStep(7);
    setError("");
  }

  // ── Download receipt ───────────────────────
  async function downloadReceipt() {
    const b = submitted;
    if (!b) return;
    const { jsPDF } = await import("jspdf");
    const html2canvas = (await import("html2canvas")).default;

    const toBase64 = async (url) => {
      const resp = await fetch(url);
      const blob = await resp.blob();
      return new Promise((r) => {
        const reader = new FileReader();
        reader.onloadend = () => r(reader.result);
        reader.readAsDataURL(blob);
      });
    };
    let logoSrc = null;
    try { logoSrc = await toBase64(receiptLogoSrc); } catch { logoSrc = null; }

    const payInfo = b.paymentInfo || {};
    let cardNumber = null;
    if (payInfo.cardLast4) cardNumber = { number: `****${payInfo.cardLast4}` };

    const pmLabel = payInfo?.upiId ? RECEIPT_LABELS.upi : payInfo?.bankName ? RECEIPT_LABELS.netBanking : payInfo?.cardLast4 ? RECEIPT_LABELS.debitCreditCard : b.paymentMethod || RECEIPT_LABELS.payByReward;
    const isPayByReward = b.paymentMethod === RECEIPT_LABELS.payByReward || b.rewardDeduction >= (b.totalAmount || 0);

    const html = generateReceiptHTML({
      item: {
        price: b.baseAmount,
        label: RECEIPT_LABELS.scheduleMeeting,
        data: b.typeLabel || RECEIPT_LABELS.visaConsultation,
        countryName: "",
        validity: "",
        type: "service",
      },
      quantity: 1,
      payMethod: b.paymentMethod || "reward",
      billingDetails: {
        firstName: b.name || "",
        lastName: "",
        email: b.email || "",
        country: "",
        phone: b.phone || "",
        phoneCode: b.phoneCode || "",
      },
      orderId: b.id,
      currency: "INR",
      appliedDiscount: isPayByReward ? 0 : (b.rewardDeduction || 0),
      logoSrc,
      pdf: true,
      upiId: payInfo.upiId || "",
      bank: payInfo.bankName || "",
      card: cardNumber,
      simType: "service",
      inr: true,
      serviceDetailRows: [
        ...(b.date && b.slot ? [`Date & Time: ${b.date}, ${b.slot}`] : b.date ? [`Date: ${b.date}`] : b.slot ? [`Time: ${b.slot}`] : []),
        ...(b.durationLabel ? [`Duration: ${b.durationLabel}`] : []),
      ],
      overrides: {
        convFee: b.convFee || 0,
        subtotal: b.baseAmount,
        tax: b.gstAmount,
        discount: isPayByReward ? 0 : (b.rewardDeduction || 0),
        total: b.totalAmount,
        subtotalLabel: PRICE_LABELS.consultationFee,
        hideSubtotal: false,
        govtFee: null,
        paymentMethodLabel: pmLabel,
        extraRows: [
          ...(isPayByReward && b.rewardDeduction ? [{ label: RECEIPT_LABELS.paidByReward, value: `\u20b9${Math.round(b.rewardDeduction).toLocaleString("en-IN")}` }] : []),
        ],
      },
    });

    const container = document.createElement("div");
    container.innerHTML = html;
    container.style.position = "fixed";
    container.style.left = "-9999px";
    container.style.top = "0";
    container.style.width = "210mm";
    container.style.background = "#f5f9ff";
    document.body.appendChild(container);
    const el = container.querySelector(".Receipt-card") || container.firstElementChild;
    const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false, backgroundColor: "#f5f9ff" });
    document.body.removeChild(container);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    pdf.save(`${RECEIPT_LABELS.filenamePrefix}-${b.id}.pdf`);
  }

  // ── Wallet data ─────────────────────────────
  function loadWalletData() {
    const session = user;
    if (session) {
      const wallet = getWallet(session.email);
      setRewardBalance(wallet.redeemable?.balance ?? 0);
      setNonRedeemableBalance(wallet.nonRedeemable?.balance ?? 0);
      setIsLoggedIn(true);
    } else {
      setRewardBalance(0);
      setNonRedeemableBalance(0);
      setIsLoggedIn(false);
    }
  }

  useEffect(() => { loadWalletData(); }, []);

  // ── Reward helpers ──────────────────────────
  const totalRewardBalance = rewardBalance + nonRedeemableBalance;

  const maxRewardAllowed = Math.min(
    Math.round(preTaxAmount * REWARD_CONSTANTS.MAX_REWARD_PERCENTAGE / 100),
    totalRewardBalance
  );

  const rewardDiscountNum = Math.max(0, Number(rewardDiscount) || 0);

  const finalPayable = payByReward
    ? 0
    : Math.max(0, totalAmount - rewardDiscountNum);

  function validateRewardDiscount(value) {
    const num = Number(value);
    if (value === "" || isNaN(num)) return REWARD_CONSTANTS.VALIDATION_MESSAGES.INVALID_AMOUNT;
    if (num < 0) return REWARD_CONSTANTS.VALIDATION_MESSAGES.NEGATIVE_AMOUNT;
    if (num > preTaxAmount * REWARD_CONSTANTS.MAX_REWARD_PERCENTAGE / 100) {
      return REWARD_CONSTANTS.VALIDATION_MESSAGES.EXCEEDS_MAX_PERCENTAGE
        .replace("{max}", REWARD_CONSTANTS.MAX_REWARD_PERCENTAGE);
    }
    if (num > totalRewardBalance) {
      return REWARD_CONSTANTS.VALIDATION_MESSAGES.EXCEEDS_BALANCE
        .replace("{balance}", totalRewardBalance);
    }
    if (num > totalAmount) {
      return REWARD_CONSTANTS.VALIDATION_MESSAGES.EXCEEDS_TOTAL;
    }
    return "";
  }

  function handleRewardDiscountChange(value) {
    setPayByReward(false);
    const cleaned = value.replace(/[^0-9]/g, "");
    if (cleaned === "") { setRewardDiscount(""); return; }
    const err = validateRewardDiscount(cleaned);
    if (err && Number(cleaned) > 0) {
      setError(err);
      const clampedMax = Math.min(
        Math.round(preTaxAmount * REWARD_CONSTANTS.MAX_REWARD_PERCENTAGE / 100),
        totalRewardBalance,
        totalAmount
      );
      setRewardDiscount(String(Math.min(Number(cleaned), clampedMax)));
      return;
    }
    setError("");
    setRewardDiscount(cleaned);
  }

  function handlePayByRewardToggle() {
    const newVal = !payByReward;
    setPayByReward(newVal);
    if (newVal) {
      setRewardDiscount("");
      setPaymentMethod("");
      setPaymentDetails(PAYMENT_DETAILS_DEFAULT);
    }
  }

  async function handleRewardPayment() {
    const session = user;
    if (!session || totalRewardBalance < totalAmount) return;
    const fullPrice = totalAmount;

    setPayByReward(true);
    spendRewardMoney(
      session.email,
      fullPrice,
      SPEND_DESCRIPTIONS.payByReward
    );

    const booking = {
      id:          genBookingId(),
      type:        meetingType,
      typeLabel:   selectedType?.label || meetingType,
      duration,
      durationLabel: selectedDuration?.label,
      paymentMethod: "Pay by Reward",
      paymentInfo: { rewardUsed: fullPrice },
      baseAmount,
      convFee,
      gstAmount,
      totalAmount: fullPrice,
      rewardDeduction: fullPrice,
      date,
      slot,
      expert:      selectedExpert?.name,
      ...form,
      bookedAt:    new Date().toISOString(),
    };
    try {
      await saveBooking(booking, user?.email);
    } catch (err) {
      const data = err?.response?.data;
      const msgs = data?.errors?.join("; ") || data?.message || err?.message || "Booking failed";
      setError(msgs);
      return;
    }
    loadWalletData();
    setSubmitted(booking);
    setStep(7);
    setError("");
  }

  // ── Reset ──────────────────────────────────
  function handleReset() {
    setStep(1);
    setMeetingType("");
    setDuration("");
    setDate("");
    setSlot("");
    setPaymentMethod("");
    setPaymentDetails(PAYMENT_DETAILS_DEFAULT);
    setForm(getFormDefaults());
    setConsent(false);
    setSubmitted(null);
    setError("");
    setRewardDiscount("");
    setPayByReward(false);
    loadWalletData();
  }

  // ──────────────────────────────────────────
  return (
    <div className="sm-page">
      <div className="sm-bg">
        <div className="sm-orb sm-orb--1" />
        <div className="sm-orb sm-orb--2" />
        <div className="sm-orb sm-orb--3" />
      </div>

      <div className={`sm-inner${step === 2 ? " sm-inner--step2" : ""}`}>

        {/* ── Header ── */}
        <div className="sm-head" data-aos="fade-down">
          <h1 className="sm-head__title">{PAGE_CONTENT.header.title}</h1>
          <p className="sm-head__sub">{PAGE_CONTENT.header.subtitle?.replace("{workingHours}", COMPANY_INFO.workingHours)}</p>

          {/* Trust badges */}
          <div className="sm-trust-row">
            {TRUST_BADGES.map(b => (
              <span key={b.label} className="sm-trust-badge">
                <span>{resolveIcon(b.icon)}</span> {b.label}
              </span>
            ))}
          </div>
        </div>

        {/* ── Stepper ── */}
        {/* {step < 7 && (
          <div className="sm-stepper" data-aos="fade-up">
            {STEPS.map((label, i) => (
              <React.Fragment key={label}>
                <div className={`sm-step${step === i + 1 ? " sm-step--active" : ""}${step > i + 1 ? " sm-step--done" : ""}`}>
                  <div className="sm-step__circle">{step > i + 1 ? "✓" : i + 1}</div>
                  <span className="sm-step__label">{label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`sm-step__line${step > i + 1 ? " sm-step__line--done" : ""}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )} */}

        {/* ── Card ── */}
        <div className={`sm-card${step === 2 ? " sm-card--duration" : ""}${step !== 2 ? " sm-card--narrow" : ""}`}>

          {/* ════ STEP 1 — Meeting Type ════ */}
          {step === 1 && (
            <>
              <div className="sm-card__title">{STEPS[0]}</div>
              <div className="sm-type-grid">
                {meetingTypes.map(t => (
                  <button
                    key={t.id}
                    className={`sm-type-card${meetingType === t.id ? " sm-type-card--active" : ""}`}
                    onClick={() => { setMeetingType(t.id); setError(""); }}
                  >
                    <span className="sm-type-card__icon">{resolveIcon(t.icon)}</span>
                    <span className="sm-type-card__label">{t.label}</span>
                    <span className="sm-type-card__desc">{t.desc}</span>
                    {meetingType === t.id && <span className="sm-type-card__check">✓</span>}
                  </button>
                ))}
              </div>
              {error && <p className="sm-error">⚠ {error}</p>}
              <button className="sm-btn-primary" onClick={handleStep1Next}>
                {PAGE_CONTENT.buttons.continue}
              </button>
            </>
          )}

          {/* ════ STEP 2 — Duration & Time ════ */}
          {step === 2 && (
            <>
              <button className="sm-back-arrow" onClick={() => goBack(2)}>←</button>
              <div className="sm-card__title">{STEPS[1]}</div>

              <div className="sm-step2-row">
                {/* Left — Expert card */}
                <div className="sm-step2-col sm-step2-col--expert">
                  {meetingType && (
                    <div className="sm-expert-card">
                      <img className="sm-expert-card__img" src={scheduleMeetingImg} alt={IMAGE_ALT.expert} />
                      <div className="sm-expert-card__name">{selectedExpert.name}</div>
                      {/* <div className="sm-expert-card__title">{selectedExpert.title}</div> */}
                    </div>
                  )}
                </div>

                {/* Right — Duration, date, slots */}
                <div className="sm-step2-col sm-step2-col--right">
                  <div className="sm-input-group sm-input-group--duration">
                    <label className="sm-label">{FORM_LABELS.meetingDuration}</label>
                    <div className="sm-duration-row sm-duration-row--desktop">
                      {durations.map(d => (
                        <button
                          key={d.id}
                          className={`sm-duration-btn${duration === d.id ? " sm-duration-btn--active" : ""}`}
                          onClick={() => { setDuration(d.id); setError(""); }}
                        >
                          <strong>{d.label}</strong>
                          <span>{d.desc}</span>
                          <span className={`sm-duration-price${d.price === "Free" ? " sm-duration-price--free" : " sm-duration-price--paid"}`}>
                            {d.price === "Free" ? PRICE_LABELS.free : `₹${d.amount}`}
                          </span>
                        </button>
                      ))}
                    </div>
                    <select className="sm-input sm-select sm-duration-select--mobile" value={duration} onChange={e => { setDuration(e.target.value); setError(""); }}>
                      <option value="">Select duration</option>
                      {durations.map(d => (
                        <option key={d.id} value={d.id}>{d.label} - {d.desc} - {d.price === "Free" ? PRICE_LABELS.free : `₹${d.amount}`}</option>
                      ))}
                    </select>
                  </div>

                  <div className="sm-input-group">
                    <label className="sm-label">{FORM_LABELS.preferredDate} <span className="sm-label-hint">{FORM_LABELS.dateHint}</span></label>
                    <input
                      className="sm-input"
                      type="date"
                      value={date}
                      min={getMinDate()}
                      max={getMaxDate()}
                      onChange={e => { setDate(e.target.value); setSlot(""); setError(""); }}
                    />
                  </div>

                  {/* Time slots */}
                  <div className="sm-input-group">
                    <label className="sm-label">{FORM_LABELS.availableSlots}</label>
                    <div className="sm-slots-grid">
                      {slots.map(s => (
                        <button
                          key={s}
                          className={`sm-slot${slot === s ? " sm-slot--active" : ""}`}
                          onClick={() => { setSlot(s); setError(""); }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    <select className="sm-input sm-select sm-slots-select--mobile" value={slot} onChange={e => { setSlot(e.target.value); setError(""); }}>
                      <option value="">Select time slot</option>
                      {slots.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {error && <p className="sm-error">⚠ {error}</p>}
              <div className="sm-btn-row sm-btn-row--end">
                <button className="sm-btn-primary" onClick={handleStep2Next}>{PAGE_CONTENT.buttons.continue}</button>
              </div>
            </>
          )}

          {/* ════ STEP 3 — Contact Details ════ */}
          {step === 3 && (
            <>
              <button className="sm-back-arrow" onClick={() => goBack(3)}>←</button>
              <div className="sm-card__title">{STEPS[2]}</div>

              <div className="sm-form-grid">
                {(contactDetails.length > 0 ? contactDetails : [
                  { key: 'name', label: 'Full Name *', required: 'Yes', placeholder: 'Your full name', type: 'text' },
                  { key: 'email', label: 'Email Address *', required: 'Yes', placeholder: 'you@email.com', type: 'email' },
                  { key: 'phone', label: 'Phone Number *', required: 'Yes', placeholder: '98765 43210', type: 'tel' },
                  { key: 'country', label: 'Country', required: 'No', placeholder: 'Enter your country name', type: 'text' },
                  { key: 'note', label: 'Additional Notes', required: 'No', placeholder: 'Anything specific you\'d like us to prepare for...', type: 'textarea' },
                ]).map(field => {
                  const isPhone = field.key === 'phone';
                  const isTextarea = field.type === 'textarea';
                  const isRequired = field.required === 'Yes';
                  const fieldValue = form[field.key] || '';
                  return (
                    <div key={field.key} className={`sm-input-group${isTextarea ? ' sm-input-group--full' : ''}`}>
                      <label className="sm-label">
                        {field.label}
                        {!isRequired && <span className="sm-label-hint"> (optional)</span>}
                      </label>
                      {isPhone ? (
                        <div className="sm-phone-row">
                          <select className="sm-input sm-phone-code sm-select" name="phoneCode" value={form.phoneCode} onChange={setF}>
                            {countryCodes.map(c => (
                              <option key={c.code} value={c.code}>{c.label}</option>
                            ))}
                          </select>
                          <input
                            className="sm-input sm-phone-input"
                            name="phone"
                            type="tel"
                            value={fieldValue}
                            onChange={setF}
                            placeholder={field.placeholder}
                            autoComplete="tel"
                          />
                        </div>
                      ) : isTextarea ? (
                        <textarea
                          className="sm-input sm-textarea"
                          name={field.key}
                          value={fieldValue}
                          onChange={setF}
                          placeholder={field.placeholder}
                          rows={3}
                        />
                      ) : (
                        <input
                          className="sm-input"
                          name={field.key}
                          type={field.type || 'text'}
                          value={fieldValue}
                          onChange={setF}
                          placeholder={field.placeholder}
                          autoComplete={field.key === 'email' ? 'email' : field.key === 'name' ? 'name' : field.key === 'country' ? 'country' : undefined}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {error && <p className="sm-error">⚠ {error}</p>}
              <div className="sm-btn-row sm-btn-row--end">
                <button className="sm-btn-primary" onClick={handleStep3Next}>{PAGE_CONTENT.buttons.continue}</button>
              </div>
            </>
          )}

          {/* ════ STEP 4 — Review Your Booking ════ */}
          {step === 4 && (
            <>
              <button className="sm-back-arrow" onClick={() => goBack(4)}>←</button>
              <div className="sm-card__title">{STEPS[3]}</div>

              <div className="sm-summary-card">
                <div className="sm-summary-row">
                  <span>{SUMMARY_LABELS.service}</span>
                  <strong>{selectedType?.label}</strong>
                </div>
                <div className="sm-summary-row">
                  <span>{SUMMARY_LABELS.duration}</span>
                  <strong>{durations.find(d => d.id === duration)?.label}</strong>
                </div>
                <div className="sm-summary-row">
                  <span>{SUMMARY_LABELS.date}</span>
                  <strong>{formatDateShort(date)}</strong>
                </div>
                <div className="sm-summary-row">
                  <span>{SUMMARY_LABELS.time}</span>
                  <strong>{slot}</strong>
                </div>
                {(contactDetails.length > 0 ? contactDetails : [
                  { key: 'name', label: 'Full Name *', required: 'Yes' },
                  { key: 'email', label: 'Email Address *', required: 'Yes' },
                  { key: 'phone', label: 'Phone Number *', required: 'Yes' },
                  { key: 'country', label: 'Country', required: 'No' },
                ]).filter(f => f.key !== 'note').map(field => {
                  const displayLabel = field.label.replace(/\s*\*?$/, '');
                  let displayValue;
                  if (field.key === 'phone') {
                    displayValue = `${form.phoneCode} ${form.phone}`;
                  } else {
                    displayValue = form[field.key] || '';
                  }
                  if (!displayValue) return null;
                  return (
                    <div key={field.key} className="sm-summary-row">
                      <span>{displayLabel}</span>
                      <strong>{displayValue}</strong>
                    </div>
                  );
                })}
                {form.note && (
                  <div className="sm-summary-row sm-summary-row--note">
                    <span>{FORM_LABELS.additionalNotes}</span>
                    <strong>{form.note}</strong>
                  </div>
                )}
                <div className="sm-summary-row sm-summary-row--total">
                  <span>{SUMMARY_LABELS.total}</span>
                  <strong>{durations.find(d => d.id === duration)?.price === "Free" ? PRICE_LABELS.free : `₹${durations.find(d => d.id === duration)?.amount}`}</strong>
                </div>
              </div>

              {error && <p className="sm-error">⚠ {error}</p>}
              <div className="sm-btn-row sm-btn-row--end">
                <button className="sm-btn-primary" onClick={handleStep4Next}>{PAGE_CONTENT.buttons.proceedToConsent}</button>
              </div>
            </>
          )}

          {/* ════ STEP 5 — Consent & Agreement ════ */}
          {step === 5 && (
            <>
              <button className="sm-back-arrow" onClick={() => goBack(5)}>←</button>
              <div className="sm-card__title">{STEPS[4]}</div>

              <div className="sm-consent">
                <div className="sm-consent__box">
                  {(() => {
                    const consentForm = getConsentForm(duration, durations, consentFree, consentPaid);
                    return consentForm.sections.map((section, index) => (
                      <div key={index} className="sm-consent__section">
                        <strong>{section.title}</strong>
                        {section.type === "paragraph" && (
                          <p>{section.content}</p>
                        )}
                        {section.type === "list" && (
                          <ul>
                            {section.content.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        )}
                        {section.type === "agreement" && (
                          <>
                            <p>{section.content}</p>
                            <ul>
                              {section.listItems.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    ));
                  })()}
                </div>
                <label className="sm-consent__checkbox">
                  <span className="sm-consent__check-wrap">
                    <input type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); setError(""); }} />
                    <span className="sm-consent__checkmark">{consent ? "✓" : ""}</span>
                  </span>
                  <span className="sm-consent__label">{getConsentForm(duration, durations, consentFree, consentPaid).checkboxLabel}</span>
                </label>
              </div>

              {error && <p className="sm-error">⚠ {error}</p>}
              <div className="sm-btn-row sm-btn-row--end">
                <button className="sm-btn-primary" disabled={!consent} onClick={handleStep5Next}>{PAGE_CONTENT.buttons.continue}</button>
              </div>
            </>
          )}

          {/* ════ STEP 6 — Payment ════ */}
          {step === 6 && (
            <>
              <button className="sm-back-arrow" onClick={() => goBack(6)}>←</button>
              <div className="sm-card__title">{STEPS[5]}</div>

              <div className="sm-payment-columns">
                <div className="sm-payment-col sm-payment-col--left">
                  <PaymentForm
                    selectedMethod={paymentMethod}
                    onMethodSelect={(id) => { setPaymentMethod(id); setPaymentDetails(PAYMENT_DETAILS_DEFAULT); setError(""); }}
                    details={paymentDetails}
                    onDetailChange={setPayF}
                    walletBalances={{ redeemable: rewardBalance, nonRedeemable: nonRedeemableBalance }}
                    requiredAmount={totalAmount}
                    onRewardPay={handleRewardPayment}
                  />
                </div>
                <div className="sm-payment-col sm-payment-col--right">
                  {/* Price Breakdown */}
                  {baseAmount > 0 && (
                    <div className="sm-price-breakdown">
                  <div className="sm-price-row">
                    <span>{PRICE_LABELS.consultationFee}</span>
                    <span>₹{baseAmount}</span>
                  </div>
                  {convFee > 0 && (
                    <div className="sm-price-row">
                      <span>{PRICE_LABELS.convenienceFee}</span>
                      <span>₹{convFee}</span>
                    </div>
                  )}
                  <div className="sm-price-row">
                    <span>{PRICE_LABELS.gst.replace("{rate}", GST_RATE)}</span>
                    <span>₹{gstAmount}</span>
                  </div>
                  {rewardDiscountNum > 0 && !payByReward && (
                    <div className="sm-price-row sm-price-row--discount">
                      <span>{PRICE_LABELS.rewardDiscount}</span>
                      <span>- ₹{rewardDiscountNum}</span>
                    </div>
                  )}
                  {payByReward && (
                    <div className="sm-price-row sm-price-row--discount">
                      <span>{PRICE_LABELS.payByReward}</span>
                      <span>- ₹{totalAmount}</span>
                    </div>
                  )}
                  <div className={`sm-price-row ${payByReward ? "sm-price-row--free" : "sm-price-row--total"}`}>
                    <span>{payByReward ? PRICE_LABELS.amountToPay : PRICE_LABELS.total}</span>
                    <span>{payByReward ? PRICE_LABELS.free : `₹${finalPayable}`}</span>
                  </div>
                </div>
              )}

              {/* ── Reward Discount Section ── */}
              {isLoggedIn && baseAmount > 0 && paymentMethod && !payByReward && paymentMethod !== "reward" && (
                <div className="sm-reward-section">
                  <div className="sm-reward-section__title">{REWARD_LABELS.title}</div>
                  <div className="sm-reward-balance-row">
                    <span className="sm-reward-balance-label">{REWARD_LABELS.totalBalance}</span>
                    <span className="sm-reward-balance-value">₹{totalRewardBalance}</span>
                  </div>
                  <div className="sm-reward-balance-row">
                    <span className="sm-reward-balance-label">{REWARD_LABELS.maxDiscount.replace("{max}", REWARD_CONSTANTS.MAX_REWARD_PERCENTAGE)}</span>
                    <span className="sm-reward-balance-value">₹{maxRewardAllowed}</span>
                  </div>
                  <div className="sm-reward-input-row">
                    <label className="sm-label">{REWARD_LABELS.enterAmount}</label>
                    <input
                      className={`sm-input sm-reward-input ${error && rewardDiscountNum > 0 ? "sm-input--error" : ""}`}
                      type="text"
                      inputMode="numeric"
                      placeholder={PLACEHOLDERS.rewardAmount}
                      value={rewardDiscount}
                      onChange={(e) => handleRewardDiscountChange(e.target.value)}
                      disabled={payByReward}
                    />
                  </div>
                  <p className="sm-reward-hint">{REWARD_LABELS.redeemableFirst}</p>
                  {payByReward && (
                    <p className="sm-reward-hint">{REWARD_LABELS.clearPayByReward}</p>
                  )}
                </div>
              )}

              {/* ── Sign In to Unlock Rewards ── */}
              {!isLoggedIn && baseAmount > 0 && paymentMethod && paymentMethod !== "reward" && (
                <div className="sm-reward-section sm-reward-section--locked">
                  <div className="sm-reward-section__title sm-reward-section__title--locked">
                    <span className="sm-reward-lock-icon">🔒</span> {REWARD_LABELS.rewards}
                  </div>
                  <p className="sm-reward-locked-text">{REWARD_CONSTANTS.LOGIN_MESSAGES.SIGN_IN_PROMPT}</p>
                  <button className="sm-btn-outline sm-reward-signin-btn" onClick={() => setShowLoginOverlay(true)}>
                    {REWARD_CONSTANTS.BUTTON_LABELS.SIGN_IN}
                  </button>
                </div>
              )}
                </div>
              </div>

              {error && <p className="sm-error">⚠ {error}</p>}
              <div className="sm-btn-row sm-btn-row--end">
                <button className="sm-btn-primary" onClick={handleStep6Next}>
                  {payByReward || paymentMethod === "reward"
                    ? REWARD_LABELS.payByReward
                    : PAGE_CONTENT.buttons.pay.replace("{amount}", finalPayable)}
                </button>
              </div>
            </>
          )}

          {/* ════ STEP 7 — Confirmation ════ */}
          {step === 7 && submitted && (
            <div className="sm-confirm">
              {/* <div className="sm-confirm__icon">✓</div> */}
              <div className="sm-confirm__title">{PAGE_CONTENT.confirmation.title}</div>
              <p className="sm-confirm__sub">
                {PAGE_CONTENT.confirmation.subtitle.replace("{email}", submitted.email)}
              </p>
              <div className="sm-confirm__details">
                {(contactDetails.length > 0 ? contactDetails : [
                  { key: 'name', label: 'Full Name *', required: 'Yes' },
                  { key: 'email', label: 'Email Address *', required: 'Yes' },
                  { key: 'phone', label: 'Phone Number *', required: 'Yes' },
                  { key: 'country', label: 'Country', required: 'No' },
                ]).filter(f => f.key !== 'note').map(field => {
                  const displayLabel = field.label.replace(/\s*\*?$/, '');
                  let displayValue;
                  if (field.key === 'phone') {
                    displayValue = `${submitted.phoneCode} ${submitted.phone}`;
                  } else {
                    displayValue = submitted[field.key] || '';
                  }
                  if (!displayValue) return null;
                  return (
                    <div key={field.key} className="sm-confirm__row">
                      <span>{displayLabel}</span>
                      <strong>{displayValue}</strong>
                    </div>
                  );
                })}
                {submitted.note && (
                  <div className="sm-confirm__row sm-confirm__row--note">
                    <span>{FORM_LABELS.additionalNotes}</span>
                    <strong>{submitted.note}</strong>
                  </div>
                )}
                <div className="sm-confirm__divider" />
                <div className="sm-confirm__row">
                  <span>{SUMMARY_LABELS.meetingType}</span>
                  <strong>{submitted.typeLabel}</strong>
                </div>
                <div className="sm-confirm__row">
                  <span>{SUMMARY_LABELS.duration}</span>
                  <strong>{durations.find(d => d.id === submitted.duration)?.label}</strong>
                </div>
                <div className="sm-confirm__row">
                  <span>{SUMMARY_LABELS.date}</span>
                  <strong>{formatDateLong(submitted.date)}</strong>
                </div>
                <div className="sm-confirm__row">
                  <span>{SUMMARY_LABELS.time}</span>
                  <strong>{submitted.slot} {COMPANY_INFO.timezone}</strong>
                </div>
                <div className="sm-confirm__row">
                  <span>{SUMMARY_LABELS.expert}</span>
                  <strong>{submitted.expert}</strong>
                </div>
                <div className="sm-confirm__row">
                  <span>{SUMMARY_LABELS.paymentMethod}</span>
                  <strong>{submitted.paymentMethod || RECEIPT_LABELS.payByReward}</strong>
                </div>
                {submitted.rewardDiscount > 0 && (
                  <div className="sm-confirm__row">
                    <span>{SUMMARY_LABELS.rewardDiscount}</span>
                    <strong>- ₹{submitted.rewardDiscount}</strong>
                  </div>
                )}
                {submitted.rewardDeduction > 0 && (
                  <div className="sm-confirm__row">
                    <span>{SUMMARY_LABELS.rewardUsed}</span>
                    <strong>₹{submitted.rewardDeduction}</strong>
                  </div>
                )}
                {submitted.baseAmount > 0 || submitted.rewardDeduction > 0 ? (
                  <>
                    <div className="sm-confirm__row">
                      <span>{SUMMARY_LABELS.consultationFee}</span>
                      <strong>₹{submitted.baseAmount > 0 ? submitted.baseAmount : totalAmount}</strong>
                    </div>
                    {submitted.convFee > 0 && (
                      <div className="sm-confirm__row">
                        <span>{SUMMARY_LABELS.convenienceFee}</span>
                        <strong>₹{submitted.convFee}</strong>
                      </div>
                    )}
                    <div className="sm-confirm__row">
                      <span>{SUMMARY_LABELS.gst.replace("{rate}", GST_RATE)}</span>
                      <strong>₹{submitted.gstAmount > 0 ? submitted.gstAmount : gstAmount}</strong>
                    </div>
                    <div className="sm-confirm__row sm-confirm__row--total">
                      <span>{SUMMARY_LABELS.totalPaid}</span>
                      <strong>₹{submitted.totalAmount}</strong>
                    </div>
                  </>
                ) : (
                  submitted.paymentMethod === RECEIPT_LABELS.payByReward && (
                    <div className="sm-confirm__row sm-confirm__row--total">
                      <span>{SUMMARY_LABELS.totalPaid}</span>
                      <strong>{SUMMARY_LABELS.zeroViaRewards}</strong>
                    </div>
                  )
                )}
                {/* <div className="sm-confirm__row">
                  <span>Booking ID</span>
                  <strong>{submitted.id}</strong>
                </div> */}
              </div>
              <div className="sm-confirm__actions">
                <button className="sm-btn-primary" onClick={downloadReceipt}>{MISC_LABELS.downloadReceipt}</button>
                {/* <a href="/" className="sm-btn-outline">{PAGE_CONTENT.buttons.backToHome}</a> */}
                <button className="sm-btn-outline" onClick={handleReset}>{PAGE_CONTENT.buttons.bookAnother}</button>
              </div>
            </div>
          )}

        </div>

        {showLoginOverlay && ReactDOM.createPortal(
          <div className="sm-reward-overlay" onClick={(e) => e.target === e.currentTarget && setShowLoginOverlay(false)}>
            <div className="sm-reward-login-modal">
              <button className="sm-reward-login-close" onClick={() => setShowLoginOverlay(false)}>&#215;</button>
              <Login
                isModal
                onLoginSuccess={() => { setShowLoginOverlay(false); loadWalletData(); }}
              />
            </div>
          </div>,
          document.body
        )}

        {/* ── Contact footer ── */}
        {/* {step < 7 && (
          <div className="sm-contact-footer" data-aos="fade-up">
            <span>{PAGE_CONTENT.footer.needHelp}</span>
            <a href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</a>
            <span className="sm-summary-sep">·</span>
            <a href={COMPANY_INFO.whatsapp} target="_blank" rel="noopener noreferrer">WhatsApp Us</a>
          </div>
        )} */}

      </div>
    </div>
  );
}
